import { logEvent, LogLevel } from '@praxis/component-logging'
import { trackLog } from './otel'

export interface ErrorObject extends Object {
  info?: string
  isAxiosError?: boolean
  toJSON?: () => object
}
export interface LoggingInfo {
  message: string | Event
  url?: string
  lineNumber?: number
  columnNumber?: number
  error?: ErrorObject | string
}

/**
 * Reduce a `LoggingInfo` to a plain string message + low-cardinality
 * attributes for the OTel logs pipeline. Keeps the existing Praxis/Kibana
 * behaviour untouched while mirroring the log into OTel (so logs correlate
 * with traces/metrics). Never throws.
 */
const toOtelMessage = (loggingInfo: LoggingInfo): string => {
  const { message } = loggingInfo
  if (typeof message === 'string') {
    return message
  }
  // `message` can be a DOM `Event` (e.g. from window.onerror); fall back to a
  // stable label rather than serialising the whole event.
  return (message as Event)?.type || 'log'
}

const toOtelAttributes = (
  loggingInfo: LoggingInfo,
): Record<string, string | number | boolean> => {
  const attributes: Record<string, string | number | boolean> = {}
  if (loggingInfo.url) {
    attributes.url = loggingInfo.url
  }
  if (typeof loggingInfo.lineNumber === 'number') {
    attributes.line_number = loggingInfo.lineNumber
  }
  if (typeof loggingInfo.columnNumber === 'number') {
    attributes.column_number = loggingInfo.columnNumber
  }
  if (typeof loggingInfo.error === 'string') {
    attributes.error = loggingInfo.error
  } else if (loggingInfo.error?.info) {
    attributes.error = loggingInfo.error.info
  }
  return attributes
}

export const log = {
  error: (loggingInfo: LoggingInfo): void => {
    logEvent(loggingInfo, { level: LogLevel.Error })
    // Mirror into the OTel logs pipeline. Safe no-op until OTel is initialized.
    trackLog('error', toOtelMessage(loggingInfo), toOtelAttributes(loggingInfo))
  },
  info: (loggingInfo: LoggingInfo): void => {
    logEvent(loggingInfo, { level: LogLevel.Info })
    trackLog('info', toOtelMessage(loggingInfo), toOtelAttributes(loggingInfo))
  },
  warn: (loggingInfo: LoggingInfo): void => {
    logEvent(loggingInfo, { level: LogLevel.Warn })
    trackLog('warn', toOtelMessage(loggingInfo), toOtelAttributes(loggingInfo))
  },
}
